import {BaseMuixFilterController} from "~/components/muix-data-grid/controllers/BaseMuixFilterController.ts";
import {action, computed, makeObservable, observable, override, reaction, runInAction} from "mobx";
import type {AiChangeChart} from "~/services/classes/CategorizationHelpers.ts";
import {convertToChart} from "~/services/classes/CategorizationHelpers.ts";
import type {BagStore} from "~/stores/BagStore.tsx";
import type ProfileStore from "../../../stores/ProfileStore.tsx";
import type {MatReviewLevelStatisticsTreeSerializer} from "~/services/classes/MaterializedClasses.ts";
import type {GRPageController} from "./GRPageController.ts";
import type {GoldenRecordCluster, GoldenRecordClusterPropertyType} from "~/services/ApiTypes.ts";
import {GoldenRecordClusterType} from "~/services/ApiTypes.ts";
import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import type {GridCallbackDetails, GridFilterModel} from "@mui/x-data-grid-premium";
import type {AxiosResponse} from "axios";

const exceptionKeys = ['harmonized_name', 'properties', 'cluster_id', 'type_id'];

export class GRFilterController extends BaseMuixFilterController {
    // Cluster
    cluster_search = '';
    cluster_type: GoldenRecordClusterType = GoldenRecordClusterType.Material;
    clusters_page = 1;
    clusters_count_server: number | undefined;
    clusters_request_busy = false;
    clusters: GoldenRecordCluster[] = [];
    selected_cluster_harmonized_name: string | undefined;
    selected_cluster_id: number | undefined;
    // Property type
    property_types_search = '';
    property_types_page = 1;
    property_types_count_server: number | undefined;
    property_types_request_busy = false;
    property_types: GoldenRecordClusterPropertyType[] = [];
    isFetchingForTheFirstTime = true; // use to show loading for entire page at the first load, (material page === 1)

    constructor(
        private controller: GRPageController,
        private api: MithraMaterializedApi,
        private profile: ProfileStore,
        protected bagStore: BagStore,
    ) {
        super(controller.tableController, 'MHFC');
        makeObservable(this, {
            isFetchingForTheFirstTime: observable,
            // Overrides
            onFilterModelChange: override,
            gridFilterModel: override,
            apiGridFilterModel: override,
            removeFilter: override,
            // Cluster
            cluster_search: observable,
            selected_cluster_harmonized_name: observable,
            cluster_type: observable,
            clusters_page: observable,
            clusters_count_server: observable,
            clusters_request_busy: observable,
            clusters: observable,
            selected_cluster_id: observable,
            setClusterSearch: action,
            setClusterType: action,
            setClustersPage: action,
            setSelectedClusterInfo: action,
            updateClusterList: action,
            // Property type
            property_types_search: observable,
            property_types_page: observable,
            property_types_count_server: observable,
            property_types_request_busy: observable,
            property_types: observable,
            setPropertyTypeSearch: action,
            setPropertyTypePage: action,
            setSelectedPropertyType: action,
            updatePropertyTypeList: action,
            // Category bars
            selectNextCategoryDown: action,
            navigateToLevel: action,
            selectedCategoryLabel: computed,
            canSelectLevelDeeper: computed,
            hasRemainingSelectionLevels: computed,
            currentSelectionStats: computed,
            selectionCharts: computed,
        });
        reaction(() => ([this.bagStore.bagId]), () => {
            this.setClustersPage(1);
        });
        // A simple workaround for debouncing cluster_search reaction
        reaction(() => ([this.cluster_search]), () => {
            if (!this.clusters_request_busy) this.setClustersPage(1);
        }, {delay: 500});
    }

    //region Cluster
    setClusterSearch(value: string) {
        this.cluster_search = value;
    }

    setClusterType(value: GoldenRecordClusterType) {
        this.cluster_type = value;
        this.setClustersPage(1);
    }

    setClustersPage(value: number) {
        this.clusters_page = value;
        this.updateClusterList(this.clusters_page === 1);
    }

    setSelectedClusterInfo(value: number | undefined, name: string | undefined) {
        this.selected_cluster_id = value;
        this.selected_cluster_harmonized_name = name
        if (this.selected_cluster_id)
            this.setPropertyTypePage(1);
    }

    setClusterLimitedAccess(id: number, value: boolean) {
        runInAction(() => this.clusters.find(c => c.id === id)!.busy = true);
        this.api.updateGoldenRecordClusters(this.bagStore.bagId, id, value).then(resp =>
            runInAction(() => {
                this.clusters.find(c => c.id === id)!.busy = false;
                this.clusters.find(c => c.id === id)!.limited_access_enabled = resp.data.limited_access_enabled;
            })
        )
    }

    updateClusterList(reset = true) {
        console.debug('updateClusterList', {reset})
        if (!reset && (this.clusters_count_server ?? 0) <= this.clusters.length)
            return;
        if (reset) {
            this.setSelectedClusterInfo(undefined, undefined);
            this.clusters_count_server = undefined;
            this.clusters = [];
        }
        const filters = [['type', String(this.cluster_type)], ['page_size', '50'], ['page', String(this.clusters_page)]];
        if (this.cluster_search) filters.push(['search', this.cluster_search]);
        this.clusters_request_busy = true;
        this.api.listGoldenRecordClusters(this.bagStore.bagId, filters).then(resp =>
            runInAction(() => {
                this.clusters_count_server = resp.data.count;
                this.clusters.push(...resp.data.results);
                this.clusters_request_busy = false;
                if (this.selected_cluster_id === undefined) {
                    const selectedCluster = this.clusters.length > 0 ? this.clusters[0] : undefined;
                    this.setSelectedClusterInfo(selectedCluster?.id, selectedCluster?.harmonized_name);
                }
                if (this.clusters_page === 1) {
                    this.isFetchingForTheFirstTime = false;
                }
            })
        );
    }

    //endregion
    //region PropertyType
    setPropertyTypeSearch(value: string) {
        this.property_types_search = value;
        this.setPropertyTypePage(1);
    }

    setPropertyTypePage(value: number) {
        this.property_types_page = value;
        this.updatePropertyTypeList(this.property_types_page === 1);
    }

    setSelectedPropertyType(id: number, selected: boolean): Promise<void> {
        if (!this.selected_cluster_id ||
            !this.property_types.find(pt => pt.id === id))
            return Promise.resolve();
        runInAction(() => this.property_types.find(pt => pt.id === id)!.busy = true);
        return this.api.updateGoldenRecordPropertyType(this.selected_cluster_id, id, selected).then(resp =>
            runInAction(() => {
                this.property_types.find(pt => pt.id === id)!.busy = false;
                this.property_types.find(pt => pt.id === id)!.selected = resp.data.selected;
                this.property_types.find(pt => pt.id === id)!.modified = resp.data.modified;
            })
        ).catch((err) => {
            runInAction(() => {
                const pt = this.property_types.find(p => p.id === id);
                if (pt) pt.busy = false;
            });
            throw err;
        }).then(() => undefined);
    }

    setBatchSelectedProperty(selected_ids: number[], not_selected_ids: number[]): Promise<AxiosResponse<never>> {
        if (!this.selected_cluster_id)
            console.error("Can't update without cluster_id, there is a failing race or something went wrong!")
        return this.api.updateBatchGoldenRecordPropertyType(this.selected_cluster_id!, selected_ids, not_selected_ids);
    }

    updatePropertyTypeList(reset = true) {
        if (!this.selected_cluster_id || (
            !reset && (this.property_types_count_server ?? 0) <= this.property_types.length))
            return;
        if (reset) {
            this.property_types_count_server = undefined;
            this.property_types = [];
        }
        const filters = [['page_size', '50'], ['page', String(this.property_types_page)]];
        if (this.property_types_search) filters.push(['search', this.property_types_search]);
        this.property_types_request_busy = true;
        this.api.listGoldenRecordPropertyType(this.selected_cluster_id, filters).then(resp =>
            runInAction(() => {
                this.property_types_count_server = resp.data.count;
                this.property_types.push(...resp.data.results);
                this.property_types_request_busy = false;
            })
        );
    }

    //endregion
    selectNextCategoryDown(selectedCategory: string) {
        this.setSelectedCategory([...this.selectedCategory, selectedCategory])
    }

    navigateToLevel(level: number) {
        this.setSelectedCategory(this.selectedCategory.slice(0, level));
    }

    get selectedCategoryLabel(): string | undefined {
        if (this.selectedCategory.length > 0) {
            return this.selectedCategory[this.selectedCategory.length - 1]
        }
        return undefined as string | undefined;
    }

    get canSelectLevelDeeper(): boolean {
        return this.bagStore.taxonomy_size > this.selectedLevel;
    }

    get hasRemainingSelectionLevels(): boolean {
        if (this.selectedLevel >= this.bagStore.taxonomy_size) {
            return false
        }
        // noinspection RedundantIfStatementJS
        if (this.selectedLevel >= 1 && !this.selectedCategoryLabel) {
            // Do not allow going deeper in uncategorized
            return false;
        }
        return true;
    }

    get currentSelectionStats(): undefined | MatReviewLevelStatisticsTreeSerializer[] {
        if (!this.hasRemainingSelectionLevels) return undefined;
        if (!this.controller._selectedStats) return undefined;
        return this.controller._selectedStats[this.controller._selectedStats.length - 1].children;
    }

    get selectionCharts(): {data: AiChangeChart[], max: number} | undefined {
        if (!this.canSelectLevelDeeper) return undefined;
        const d = this.currentSelectionStats;
        if (!d) return undefined;
        // Note that for GR we don't need old values since we don't want to show AI categorization performance
        const data = convertToChart(d.filter(d =>
            d.values['post_parts']
        ), this.profile.currencySymbol
        ).map(d => ({
            ...d,
            values: d.values.filter(v => v.dataCls === 'new')
        }));
        const COL_WIDTH = 6 / 9 // Show as width col=6, while the whole viz goes to col=9
        const max = Math.max(...data.map(D => Math.max(...D.values.map(v => v.value)))) / COL_WIDTH;
        return {data, max}
    }

    onFilterModelChange(model: GridFilterModel, details: GridCallbackDetails<'filter'>): void {
        // If difference of model with stored is on the exceptionKeys, super should not be called
        const currentItems = this.gridFilterModel.items;
        if (model.items.some(i => !exceptionKeys.includes(i.field) && !currentItems.some(j => j.field === i.field && j.value === i.value && j.operator === i.operator)))
            super.onFilterModelChange(model, details);
    }

    get gridFilterModel(): GridFilterModel {
        const filterModel = super.gridFilterModel;
        filterModel.items = filterModel.items.filter(i => !exceptionKeys.includes(i.field))
        if (this.selected_cluster_id)
            filterModel.items.push({
                id: 'harmonized_name',
                field: 'harmonized_name',
                value: (this.clusters ?? []).find(c => c.id === this.selected_cluster_id)?.harmonized_name ?? '',
                operator: 'equals'
            });
        return filterModel;
    }

    get apiGridFilterModel(): GridFilterModel {
        const filterModel = super.apiGridFilterModel;
        filterModel.items = filterModel.items.filter(i => !exceptionKeys.includes(i.field))
        // Databag filter must be present, otherwise MH would show results of all databags
        filterModel.items.push({
            id: 'databag_id',
            field: 'databag_id',
            value: this.bagStore.bagId,
            operator: 'equals'
        });
        if (this.selected_cluster_id)
            filterModel.items.push({
                id: 'cluster_id',
                field: 'cluster_id',
                value: this.selected_cluster_id,
                operator: 'equals'
            });
        return filterModel;
    }

    removeFilter(id: number | string | undefined) {
        if (id === 'harmonized_name') {
            this.setSelectedClusterInfo(undefined, undefined);
            this.controller.tableController.resetRowState();
        } else
            super.removeFilter(id);
    }
}